import { useMemo } from "react";
import { motion } from "framer-motion";
import katex from "katex";
import { cn } from "../../lib/cn";

interface EquationRendererProps {
  latex: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  onClick?: () => void;
}

function EquationRenderer({ latex, size = "md", onClick, className }: EquationRendererProps) {
  const html = useMemo(() => {
    try {
      return katex.renderToString(latex, {
        displayMode: true,
        throwOnError: false,
        trust: false,
        strict: false,
      });
    } catch {
      return `<span style="color: var(--color-soft-red)">Invalid LaTeX</span>`;
    }
  }, [latex]);

  const sizeClass = {
    sm: "text-base",
    md: "text-xl",
    lg: "text-2xl",
  }[size];

  return (
    <motion.div
      onClick={onClick}
      whileHover={onClick ? { scale: 1.01 } : undefined}
      className={cn(
        "flex items-center justify-center rounded-[var(--radius-lg)] p-4",
        "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
        "overflow-x-auto",
        sizeClass,
        onClick && "cursor-pointer hover:border-[var(--border-default)] transition-colors",
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

EquationRenderer.displayName = "EquationRenderer";

export { EquationRenderer };
export type { EquationRendererProps };
